/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { useForm as useAbyssForm } from '@uhg-abyss/web/hooks/useForm';
import { useTinRegistration } from '@src/state/tinRegistration';
import { getFileType } from '@src/state/tinRegistration/utils';
import { event } from '@uhg-abyss/web/tools/event';
import { useUser } from '@src/state/user/hooks';
import round from 'lodash/round';
import { FileUpload as AbyssFileUpload } from '@uhg-abyss/web/ui/FileUpload';
import { FormTooltip } from './FormTooltip';
import { CMS_LABEL } from './constants';

const ONE_MB = 1024 * 1024;

const Label = styled('div', {
  fontWeight: ({ theme }) => {
    return theme.fontWeights.medium;
  },
  fontSize: ({ theme }) => {
    return theme.fontSizes.size400;
  },
  color: '$gray8',
});

const LabelCMS = styled('div', {
  fontWeight: ({ theme }) => {
    return theme.fontWeights.bold;
  },
  fontSize: '16px',
});

const AlignTooltip = styled('span', {
  marginLeft: '12px',
  verticalAlign: 'sub',
});

const AlignRemoveIcon = styled('div', {
  float: 'left',
  marginLeft: '16px',
  verticalAlign: 'middle',
  cursor: 'pointer',
});

const SubTextStyles = styled('div', {
  fontSize: '14px',
  fontStyle: 'italic',
  lineHeight: 'normal',
  paddingLeft: '3px',
  color: '$gray8',
  width: '580px',
  // marginTop: '10px',
});

const AlignRemoveText = styled('span', {
  marginLeft: '2px',
  verticalAlign: 'text-bottom',
});

const File = styled('div', {
  color: '$info1',
  fontWeight: 700,
  marginTop: '8px',
  width: '150%',
});
const FormErrorMessage = styled('span', {
  fontWeight: 600,
  fontSize: '80%',
  width: '100%',
  color: '$error1',
  display: 'inline-block',
  marginTop: '5px',
});

const UploadButton = styled('div', {
  '& [data-testid*="w9-upload-button"]': {
    width: '8vw !important', // Set your desired width here
    paddingLeft: '1px !important',
  },
});

const UploadFile = styled('div', {});

const FileName = styled('span', {
  marginLeft: '8px',
  verticalAlign: 'text-bottom',
});

const UploadError = styled('div', {
  marginTop: '4px',
});

const UploadingMessage = styled('div', {
  marginTop: '4px',
  fontSize: '13px',
  color: '$accent1',
});

const UploadSuccessMessage = styled('div', {
  marginTop: '4px',
  fontSize: '13px',
  color: '$success1',
});

export const FileUpload = ({ id, model, label, onUpload, required }) => {
  const [fileName, setFileName] = useState('');
  const [formErrorMessage, setFormErrorMessage] = useState('');
  const [isUploadDisabled, setUploadDisabled] = useState(false);
  const [fileList, setFileList] = useState();
  const { trackingNumber, isDocumentUploading, tinDraft } =
    useTinRegistration.state();
  const abyssForm = useAbyssForm();
  const registration = abyssForm.getValues('searchRegistration');
  const uploadedFile = abyssForm.getValues(model);

  const userInfo = useUser.state();
  const { uploadProof, setDocumentId, setDocumentLoading } =
    useTinRegistration.actions();
  const [uploadFailed, setUploadFailed] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const { isCheckoutUser } = userInfo;
  const ALLOWED_FILE_TYPES = ['pdf', 'jpeg', 'png', 'jpg'];
  const FILE_SIZE_LIMIT = 5;
  const FILE_NAME_LIMIT = 50;

  // FOR UPLOAD_CLIA
  const onSuccessUpload = (e) => {
    onUpload(e);
  };

  useEffect(() => {
    if (fileName !== '') {
      onUpload(true);
    }
  }, [fileName]);

  const findFileExists = () => {
    const regex = new RegExp('(?:secondaryServicing)...');
    const modelSegments = model.split('.');
    let type = '';
    if (modelSegments[1] === 'primaryServicingProof') {
      type = 'PrimaryService';
    }
    if (modelSegments[1] === 'primaryBillingProof') {
      type = 'PrimaryBilling';
    }
    if (modelSegments[1] === 'primaryRemittanceProof') {
      type = 'PrimaryRemit';
    }
    if (regex.test(modelSegments[1])) {
      const index = parseInt(modelSegments[1].slice(19, 20), 10) + 1;
      type = `SecondaryService${index}`;
    }
    if (tinDraft && tinDraft.address) {
      const addressObj = tinDraft.address.find((val) => {
        return val.type === type;
      });
      if (addressObj && addressObj.doc360Id && addressObj.fileName) return true;
      return false;
    }
    return false;
  };

  const validateUploadedFile = (file) => {
    if (!file) return false;
    const extension = file.name.split('.').pop().toLowerCase();
    const nameLength = file.name.length;
    return (
      ALLOWED_FILE_TYPES.includes(`${extension}`) &&
      file.size < FILE_SIZE_LIMIT * ONE_MB &&
      nameLength <= FILE_NAME_LIMIT &&
      file.size > 0
    );
  };

  const handleFileButton = (selectedFile) => {
    const file = selectedFile[selectedFile.length - 1];
    setFileList();
    if (!file) {
      if (label === CMS_LABEL) {
        abyssForm.setValue(model, [], {
          errors: { required: false },
          touched: false,
        });
      } else {
        abyssForm.setValue(model, [], {
          errors: { required: true },
          touched: false,
        });
        setFormErrorMessage('File upload is required.');
      }
      onSuccessUpload(false);
      setFileName('');
      setUploadDisabled(false);
      setDocumentId({ id: model, value: undefined });
      setUploadSuccess(false);
      return;
    }

    if (file.size === 0) {
      setFormErrorMessage(
        `File Name: ${file.name} is empty. Please attach a valid file.`
      );
      abyssForm.setValue(model, []);
      return;
    }
    if (file.size > FILE_SIZE_LIMIT * ONE_MB) {
      setFormErrorMessage(
        `File Name: ${file.name} is ${round(
          file.size / ONE_MB,
          1
        )} MB (Attachment is too large, maximum is ${FILE_SIZE_LIMIT} MB)`
      );
      abyssForm.setValue(model, []);
      return;
    }
    if (file.name.length > FILE_NAME_LIMIT) {
      setFormErrorMessage(
        `File Name cannot exceed ${FILE_NAME_LIMIT} characters in length.`
      );
      abyssForm.setValue(model, []);
      return;
    }

    const extension = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_FILE_TYPES.includes(extension)) {
      setFormErrorMessage(
        `File ${file.name} is not supported. Please try again.`
      );
      abyssForm.setValue(model, []);
      return;
    }
    setFormErrorMessage('');

    if (validateUploadedFile(file)) {
      abyssForm.setValue(model, file);
      setFileName(file.name);
      setUploadDisabled(true);
      setUploadFailed(false);
      const tNum = trackingNumber;
      abyssForm.validate(model, (value) => {
        const reqBody = {
          billingTin: registration.billingTin,
          requestId: registration.requestId,
          fileType: getFileType(model),
          trackingNumber: tNum,
          file: value,
          test: isCheckoutUser,
          model,
        };
        abyssForm.setValue(model, undefined, {
          errors: { required: true },
          touched: false,
        });

        setDocumentLoading({ model, value: true });
        uploadProof(
          reqBody,
          (resp) => {
            abyssForm.setValue(model, value, { touched: false });
            setDocumentLoading({ model, value: false });
            setDocumentId({ id: model, value: resp.doc360Id });
            if (resp && resp.doc360Id) {
              setUploadSuccess(true);
              onSuccessUpload(true);
            } else {
              onSuccessUpload(false);
              setDocumentLoading({ model, value: false });
              setUploadFailed(true);
              setFormErrorMessage(
                "Service unavailable: We're sorry, your upload was unsuccessful. Please try again later. (D-001)"
              );
              setFileName('');
              abyssForm.setValue(model, undefined);
              setUploadDisabled(false);
            }
          },
          (error) => {
            event('API_TRANSACTION_ERROR', {
              code: error.faultCode,
              message: error.message,
            });
            onSuccessUpload(false);
            setDocumentLoading({ model, value: false });
            setUploadFailed(true);
            setFormErrorMessage(
              "Service unavailable: We're sorry, your upload was unsuccessful. Please try again later. (D-001)"
            );
            setFileName('');
            abyssForm.setValue(model, undefined);
            setUploadDisabled(false);
          }
        );
      });
    } else {
      onSuccessUpload(false);
      setFormErrorMessage(
        'Attachment is not supported. Please try again. (D-001)'
      );
      abyssForm.setValue(model, []);
    }
  };

  useEffect(() => {
    if (uploadedFile) {
      setFileName(uploadedFile.name);
      if (uploadedFile.name) {
        setFileList([
          {
            name: uploadedFile.name,
          },
        ]);
        setUploadDisabled(true);
      }
    }
  }, [uploadedFile, fileName]);

  const errValidators = {
    fileEmpty: (file) => {
      if (label === CMS_LABEL) {
        return false;
      }
      if (!findFileExists()) {
        return !file || file.size === 0;
      }
      if (!file || file.size === 0) {
        return true;
      }
      return false;
    },
    fileSize: (file) => {
      if (file) {
        if (file.size === 0) {
          return true;
        }
        if (file.size) {
          return file.size > FILE_SIZE_LIMIT * ONE_MB;
        }
      }
      return false;
    },
    fileNameLength: (file) => {
      if (file) {
        const nameLength = file.name.length;
        return nameLength > FILE_NAME_LIMIT;
      }
      return false;
    },
    fileType: (file) => {
      if (file) {
        const extension = file.name.split('.').pop().toLowerCase();
        return !ALLOWED_FILE_TYPES.includes(`${extension}`);
      }
      return false;
    },
    required: (file) => {
      if (label === CMS_LABEL) {
        return false;
      }
      return file === null && !uploadedFile && fileName === '';
    },
  };

  const labelDefault = (
    <Label>
      {label}
      <AlignTooltip>
        <FormTooltip label={label}>
          <IconSymbol icon="info" size="18px" />
        </FormTooltip>
      </AlignTooltip>
    </Label>
  );

  const labelCMS = (
    <LabelCMS>
      {label}
      <AlignTooltip>
        <FormTooltip label={label}>
          <IconSymbol icon="info" size="18px" />
        </FormTooltip>
      </AlignTooltip>
    </LabelCMS>
  );

  let labelCont = labelDefault;
  if (label === CMS_LABEL) {
    labelCont = labelCMS;
  }

  return (
    <React.Fragment>
      <UploadFile>
        <Grid>
          <Grid.Col span="50%">
            <UploadButton>
              <AbyssFileUpload
                model={model}
                maxFileSize={5}
                noIcon
                fileList={fileList}
                dragDisabled
                label={label}
                helper={
                  <FormTooltip label={label}>
                    <IconSymbol icon="info" size="18px" />
                  </FormTooltip>
                }
                id={id}
                value={fileList}
                onChange={handleFileButton}
                isDisabled={isUploadDisabled}
                validators={{
                  ...errValidators,
                  ...(required !== undefined && { required }),
                }}
                required={required}
              />
            </UploadButton>
          </Grid.Col>
        </Grid>
      </UploadFile>
      <UploadError>
        {formErrorMessage && (
          <FormErrorMessage id={`${id}-form-error-message`}>
            {formErrorMessage}
          </FormErrorMessage>
        )}
      </UploadError>
      <SubTextStyles id={`${id}-filetext-size`}>
        File must not be password protected.
      </SubTextStyles>
      {fileName && isDocumentUploading[model] && (
        <UploadingMessage id={`${id}-uploading-message`}>
          Uploading file, please wait...
        </UploadingMessage>
      )}
      {uploadSuccess && (
        <UploadSuccessMessage id={`${id}-upload-success-message`}>
          File has been uploaded successfully.
        </UploadSuccessMessage>
      )}
    </React.Fragment>
  );
};

FileUpload.propTypes = {
  required: PropTypes.bool,
  id: PropTypes.string,
  model: PropTypes.string.isRequired,
  label: PropTypes.string,
  onUpload: PropTypes.func,
};

FileUpload.defaultProps = {
  required: false,
  id: 'address-proof-upload-input',
  label: 'Upload Address Proof',
  onUpload: (e) => {},
};
